import { useState, useEffect, useRef } from "react";

type LinkData = {
  title?: string;
  description?: string;
  image?: { url: string };
  logo?: { url: string };
};

type LinkPreviewProps = {
  url: string;
};

export const LinkPreview = ({ url }: LinkPreviewProps) => {
  const [data, setData] = useState<LinkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!url) return;

    setLoading(true);
    setData(null);
    setImgError(false);

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    fetch(`https://api.microlink.io/?url=${encodeURIComponent(url)}`, {
      signal: abortRef.current.signal,
    })
      .then((res) => res.json())
      .then((json) => {
        if (json?.status === "success" && json?.data) {
          setData(json.data);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });

    return () => {
      abortRef.current?.abort();
    };
  }, [url]);

  if (loading || !data) return null;

  const title = data.title;
  const description = data.description;
  const imageUrl = !imgError ? data.image?.url : undefined;
  const logoUrl = data.logo?.url;

  if (!title && !description) return null;

  let domain = "";
  try {
    domain = new URL(url).hostname.replace("www.", "");
  } catch {
    domain = url;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "block",
        width: "100%",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        backgroundColor: "#f9fafb",
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer",
        marginTop: 8,
      }}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt={title || ""}
          onError={() => setImgError(true)}
          style={{
            width: "100%",
            height: 200,
            objectFit: "cover",
            display: "block",
            borderBottom: "1px solid #e2e8f0",
          }}
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: imageUrl ? "10px 14px" : "10px 14px",
        }}
      >
        {!imageUrl && logoUrl && (
          <img
            src={logoUrl}
            alt=""
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          {title && (
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                color: "#111",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </div>
          )}
          {description && imageUrl && (
            <div
              style={{
                fontSize: 12,
                color: "#555",
                marginTop: 2,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {description}
            </div>
          )}
          <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
            {domain}
          </div>
        </div>
      </div>
    </a>
  );
};
